/**
 * Lighting module. Use LightingModule only.
 * Public: setScene; createAmbientLight, createDirectionalLight, createPointLight,
 * createSpotLight, createHemisphereLight, createRectAreaLight; getLight, getLightMeta, getLightIds, getAllLights, getLightsByType, hasLight;
 * removeLight, removeAllLights; configureRendererShadows.
 */

import * as THREE from "three";

export type PositionInput =
  | THREE.Vector3
  | [number, number, number]
  | { x: number; y: number; z: number };

export type ColorInput = THREE.Color | number | string;
export type ShadowMapQuality = "low" | "medium" | "high" | "ultra";
export type ShadowMapType = number;

export interface ShadowCameraConfig {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
  near?: number;
  far?: number;
}

export interface PerspectiveShadowCameraConfig {
  fov?: number;
  aspect?: number;
  near?: number;
  far?: number;
}

export interface BaseLightOptions {
  color?: ColorInput;
  intensity?: number;
  name?: string;
}
export interface AmbientLightOptions extends BaseLightOptions {}
export interface DirectionalLightOptions extends BaseLightOptions {
  position?: PositionInput;
  target?: PositionInput;
  castShadow?: boolean;
  shadowMapSize?: number;
  shadowMapQuality?: ShadowMapQuality;
  shadowMapType?: ShadowMapType;
  shadowCamera?: ShadowCameraConfig;
  shadowBias?: number;
  shadowNormalBias?: number;
  shadowRadius?: number;
}

export interface PointLightOptions extends BaseLightOptions {
  position?: PositionInput;
  distance?: number;
  decay?: number;
  castShadow?: boolean;
  shadowMapSize?: number;
  shadowMapQuality?: ShadowMapQuality;
  shadowMapType?: ShadowMapType;
  shadowCamera?: PerspectiveShadowCameraConfig;
  shadowBias?: number;
  shadowNormalBias?: number;
  shadowRadius?: number;
}

export interface SpotLightOptions extends BaseLightOptions {
  position?: PositionInput;
  target?: PositionInput;
  angle?: number;
  penumbra?: number;
  distance?: number;
  decay?: number;
  castShadow?: boolean;
  shadowMapSize?: number;
  shadowMapQuality?: ShadowMapQuality;
  shadowMapType?: ShadowMapType;
  shadowCamera?: PerspectiveShadowCameraConfig;
  shadowBias?: number;
  shadowNormalBias?: number;
  shadowRadius?: number;
}

export interface HemisphereLightOptions extends BaseLightOptions {
  skyColor?: ColorInput;
  groundColor?: ColorInput;
  position?: PositionInput;
}

export interface RectAreaLightOptions extends BaseLightOptions {
  position?: PositionInput;
  width?: number;
  height?: number;
  target?: PositionInput;
}

export interface RendererShadowConfig {
  enabled?: boolean;
  type?: ShadowMapType;
  autoUpdate?: boolean;
}

export interface LightWithMeta {
  light: THREE.Light;
  id: string;
  type: string;
  createdAt: number;
}

function toColor(color?: ColorInput): THREE.Color {
  if (!color) return new THREE.Color(0xffffff);
  if (color instanceof THREE.Color) return color;
  if (typeof color === "string") return new THREE.Color(color);
  return new THREE.Color(color);
}

function toVec3(position?: PositionInput): THREE.Vector3 {
  if (!position) return new THREE.Vector3(0, 0, 0);
  if (position instanceof THREE.Vector3) return position.clone();
  if (Array.isArray(position)) return new THREE.Vector3(position[0], position[1], position[2]);
  return new THREE.Vector3(position.x, position.y, position.z);
}

function getShadowMapSize(quality?: ShadowMapQuality): number {
  switch (quality) {
    case "low":
      return 512;
    case "medium":
      return 1024;
    case "high":
      return 2048;
    case "ultra":
      return 4096;
    default:
      return 2048;
  }
}


export class LightingModule {
  private scene?: THREE.Scene;
  private lights: Map<string, LightWithMeta> = new Map();
  private nextId: number = 0;

  constructor(scene?: THREE.Scene) {
    this.scene = scene;
  }

  /** Set scene for auto-adding lights. */
  setScene(scene: THREE.Scene): void {
    this.scene = scene;
  }

  private generateId(prefix: string = "light"): string {
    return `${prefix}-${this.nextId++}`;
  }

  /** Create AmbientLight; returns light id. */
  createAmbientLight(options: AmbientLightOptions = {}): string {
    const { color, intensity = 1, name } = options;
    const id = this.generateId("ambient");

    const light = new THREE.AmbientLight(toColor(color), intensity);
    if (name) light.name = name;

    this.lights.set(id, {
      light,
      id,
      type: "AmbientLight",
      createdAt: Date.now(),
    });

    if (this.scene) {
      this.scene.add(light);
    }

    return id;
  }

  /** Create DirectionalLight; optional shadows. Returns light id. */
  createDirectionalLight(options: DirectionalLightOptions = {}): string {
    const {
      color,
      intensity = 1,
      position,
      target,
      castShadow = false,
      shadowMapSize,
      shadowMapQuality,
      shadowMapType: _shadowMapType, // Shadow map type is set on renderer, not individual shadows
      shadowCamera,
      shadowBias,
      shadowNormalBias,
      shadowRadius,
      name,
    } = options;
    const id = this.generateId("directional");

    const light = new THREE.DirectionalLight(toColor(color), intensity);
    if (name) light.name = name;

    // Set position
    if (position) {
      light.position.copy(toVec3(position));
    }

    // Set target
    if (target) {
      light.target.position.copy(toVec3(target));
    }

    // Configure shadows
    if (castShadow) {
      light.castShadow = true;

      const mapSize =
        shadowMapSize || getShadowMapSize(shadowMapQuality || "high");
      light.shadow.mapSize.width = mapSize;
      light.shadow.mapSize.height = mapSize;
      // Note: Shadow map type is set on the renderer, not individual shadows

      // Configure shadow camera (orthographic for directional light)
      if (shadowCamera) {
        const camera = light.shadow.camera as THREE.OrthographicCamera;
        if (shadowCamera.left !== undefined) camera.left = shadowCamera.left;
        if (shadowCamera.right !== undefined) camera.right = shadowCamera.right;
        if (shadowCamera.top !== undefined) camera.top = shadowCamera.top;
        if (shadowCamera.bottom !== undefined)
          camera.bottom = shadowCamera.bottom;
        if (shadowCamera.near !== undefined) camera.near = shadowCamera.near;
        if (shadowCamera.far !== undefined) camera.far = shadowCamera.far;
        camera.updateProjectionMatrix();
      }

      if (shadowBias !== undefined) light.shadow.bias = shadowBias;
      if (shadowNormalBias !== undefined)
        light.shadow.normalBias = shadowNormalBias;
      if (shadowRadius !== undefined) light.shadow.radius = shadowRadius;
    }

    this.lights.set(id, {
      light,
      id,
      type: "DirectionalLight",
      createdAt: Date.now(),
    });

    if (this.scene) {
      this.scene.add(light);
      if (target) {
        this.scene.add(light.target);
      }
    }

    return id;
  }

  /** Create PointLight; optional shadows. Returns light id. */
  createPointLight(options: PointLightOptions = {}): string {
    const {
      color,
      intensity = 1,
      position,
      distance = 0,
      decay = 2,
      castShadow = false,
      shadowMapSize,
      shadowMapQuality,
      shadowMapType: _shadowMapType, // Shadow map type is set on renderer, not individual shadows
      shadowCamera,
      shadowBias,
      shadowNormalBias,
      shadowRadius,
      name,
    } = options;
    const id = this.generateId("point");

    const light = new THREE.PointLight(toColor(color), intensity, distance, decay);
    if (name) light.name = name;

    // Set position
    if (position) {
      light.position.copy(toVec3(position));
    }

    // Configure shadows
    if (castShadow) {
      light.castShadow = true;

      const mapSize =
        shadowMapSize || getShadowMapSize(shadowMapQuality || "high");
      light.shadow.mapSize.width = mapSize;
      light.shadow.mapSize.height = mapSize;
      // Note: Shadow map type is set on the renderer, not individual shadows

      // Configure shadow camera (perspective for point light)
      if (shadowCamera) {
        const camera = light.shadow.camera as THREE.PerspectiveCamera;
        if (shadowCamera.fov !== undefined) camera.fov = shadowCamera.fov;
        if (shadowCamera.aspect !== undefined)
          camera.aspect = shadowCamera.aspect;
        if (shadowCamera.near !== undefined) camera.near = shadowCamera.near;
        if (shadowCamera.far !== undefined) camera.far = shadowCamera.far;
        camera.updateProjectionMatrix();
      }

      if (shadowBias !== undefined) light.shadow.bias = shadowBias;
      if (shadowNormalBias !== undefined)
        light.shadow.normalBias = shadowNormalBias;
      if (shadowRadius !== undefined) light.shadow.radius = shadowRadius;
    }

    this.lights.set(id, {
      light,
      id,
      type: "PointLight",
      createdAt: Date.now(),
    });

    if (this.scene) {
      this.scene.add(light);
    }

    return id;
  }

  /** Create SpotLight; optional shadows. Returns light id. */
  createSpotLight(options: SpotLightOptions = {}): string {
    const {
      color,
      intensity = 1,
      position,
      target,
      angle = Math.PI / 3,
      penumbra = 0,
      distance = 0,
      decay = 2,
      castShadow = false,
      shadowMapSize,
      shadowMapQuality,
      shadowMapType: _shadowMapType, // Shadow map type is set on renderer, not individual shadows
      shadowCamera,
      shadowBias,
      shadowNormalBias,
      shadowRadius,
      name,
    } = options;
    const id = this.generateId("spot");

    const light = new THREE.SpotLight(
      toColor(color),
      intensity,
      distance,
      angle,
      penumbra,
      decay
    );
    if (name) light.name = name;

    // Set position
    if (position) {
      light.position.copy(toVec3(position));
    }

    // Set target
    if (target) {
      light.target.position.copy(toVec3(target));
    }

    // Configure shadows
    if (castShadow) {
      light.castShadow = true;

      const mapSize =
        shadowMapSize || getShadowMapSize(shadowMapQuality || "high");
      light.shadow.mapSize.width = mapSize;
      light.shadow.mapSize.height = mapSize;
      // Note: Shadow map type is set on the renderer, not individual shadows

      // Configure shadow camera (perspective for spot light)
      if (shadowCamera) {
        const camera = light.shadow.camera as THREE.PerspectiveCamera;
        if (shadowCamera.fov !== undefined) camera.fov = shadowCamera.fov;
        if (shadowCamera.aspect !== undefined)
          camera.aspect = shadowCamera.aspect;
        if (shadowCamera.near !== undefined) camera.near = shadowCamera.near;
        if (shadowCamera.far !== undefined) camera.far = shadowCamera.far;
        camera.updateProjectionMatrix();
      }

      if (shadowBias !== undefined) light.shadow.bias = shadowBias;
      if (shadowNormalBias !== undefined)
        light.shadow.normalBias = shadowNormalBias;
      if (shadowRadius !== undefined) light.shadow.radius = shadowRadius;
    }

    this.lights.set(id, {
      light,
      id,
      type: "SpotLight",
      createdAt: Date.now(),
    });

    if (this.scene) {
      this.scene.add(light);
      if (target) {
        this.scene.add(light.target);
      }
    }

    return id;
  }

  /** Create HemisphereLight; returns light id. */
  createHemisphereLight(options: HemisphereLightOptions = {}): string {
    const {
      skyColor,
      groundColor,
      intensity = 1,
      position,
      name,
    } = options;
    const id = this.generateId("hemisphere");

    const light = new THREE.HemisphereLight(
      toColor(skyColor || 0xffffff),
      toColor(groundColor || 0x444444),
      intensity
    );
    if (name) light.name = name;

    // Set position
    if (position) {
      light.position.copy(toVec3(position));
    }

    this.lights.set(id, {
      light,
      id,
      type: "HemisphereLight",
      createdAt: Date.now(),
    });

    if (this.scene) {
      this.scene.add(light);
    }

    return id;
  }

  /** Create RectAreaLight; returns light id. */
  createRectAreaLight(options: RectAreaLightOptions = {}): string {
    const {
      color,
      intensity = 1,
      position,
      width = 10,
      height = 10,
      target,
      name,
    } = options;
    const id = this.generateId("rectArea");

    const light = new THREE.RectAreaLight(
      toColor(color),
      intensity,
      width,
      height
    );
    if (name) light.name = name;

    // Set position
    if (position) {
      light.position.copy(toVec3(position));
    }

    // Set target
    if (target) {
      light.lookAt(toVec3(target));
    }

    this.lights.set(id, {
      light,
      id,
      type: "RectAreaLight",
      createdAt: Date.now(),
    });

    if (this.scene) {
      this.scene.add(light);
    }

    return id;
  }

  // ============================================================================
  // LIGHT MANAGEMENT METHODS
  // ============================================================================

  /** Get light by id, or null. */
  getLight(id: string): THREE.Light | null {
    const meta = this.lights.get(id);
    return meta ? meta.light : null;
  }

  /** Get light metadata by id, or null. */
  getLightMeta(id: string): LightWithMeta | null {
    return this.lights.get(id) || null;
  }

  /** All light ids. */
  getLightIds(): string[] {
    return Array.from(this.lights.keys());
  }

  /** All lights. */
  getAllLights(): THREE.Light[] {
    return Array.from(this.lights.values()).map((meta) => meta.light);
  }

  /** All lights of given type. */
  getLightsByType(type: string): THREE.Light[] {
    return Array.from(this.lights.values())
      .filter((meta) => meta.type === type)
      .map((meta) => meta.light);
  }

  /** True if light id exists. */
  hasLight(id: string): boolean {
    return this.lights.has(id);
  }

  /** Remove light by id; returns true if removed. */
  removeLight(id: string): boolean {
    const meta = this.lights.get(id);
    if (!meta) return false;

    const { light } = meta;

    // Remove from scene
    if (this.scene) {
      this.scene.remove(light);

      // Remove target if it exists (for DirectionalLight and SpotLight)
      if (
        light instanceof THREE.DirectionalLight ||
        light instanceof THREE.SpotLight
      ) {
        this.scene.remove(light.target);
      }
    }

    // Dispose light
    this.disposeLight(light);

    // Remove from registry
    this.lights.delete(id);

    return true;
  }

  /** Remove and dispose all lights. */
  removeAllLights(): void {
    const ids = Array.from(this.lights.keys());
    ids.forEach((id) => this.removeLight(id));
  }

  /** Add light to scene by id; returns true if added. */
  addLightToScene(id: string): boolean {
    const meta = this.lights.get(id);
    if (!meta || !this.scene) return false;

    const { light } = meta;

    if (!this.scene.children.includes(light)) {
      this.scene.add(light);

      // Add target if it exists
      if (
        light instanceof THREE.DirectionalLight ||
        light instanceof THREE.SpotLight
      ) {
        if (!this.scene.children.includes(light.target)) {
          this.scene.add(light.target);
        }
      }
    }

    return true;
  }

  /** Remove light from scene by id (keeps in registry); returns true if removed. */
  removeLightFromScene(id: string): boolean {
    const meta = this.lights.get(id);
    if (!meta || !this.scene) return false;

    const { light } = meta;

    if (this.scene.children.includes(light)) {
      this.scene.remove(light);

      // Remove target if it exists
      if (
        light instanceof THREE.DirectionalLight ||
        light instanceof THREE.SpotLight
      ) {
        this.scene.remove(light.target);
      }
    }

    return true;
  }

  // ============================================================================
  // SHADOW MANAGEMENT METHODS
  // ============================================================================

  /** Configure shadow map for a light by id; returns true if applied. */
  configureLightShadows(
    id: string,
    options: {
      castShadow?: boolean;
      mapSize?: number;
      quality?: ShadowMapQuality;
      type?: ShadowMapType;
      bias?: number;
      normalBias?: number;
      radius?: number;
      camera?: ShadowCameraConfig | PerspectiveShadowCameraConfig;
    }
  ): boolean {
    const meta = this.lights.get(id);
    if (!meta) return false;

    const { light } = meta;

    // Check if light supports shadows
    if (
      !(light instanceof THREE.DirectionalLight) &&
      !(light instanceof THREE.PointLight) &&
      !(light instanceof THREE.SpotLight)
    ) {
      return false;
    }

    if (options.castShadow !== undefined) {
      light.castShadow = options.castShadow;
    }

    if (light.castShadow) {
      const mapSize =
        options.mapSize ||
        getShadowMapSize(options.quality || "high");
      light.shadow.mapSize.width = mapSize;
      light.shadow.mapSize.height = mapSize;

      // Note: Shadow map type is set on the renderer, not individual shadows
      // The type option is stored for renderer configuration

      if (options.bias !== undefined) {
        light.shadow.bias = options.bias;
      }

      if (options.normalBias !== undefined) {
        light.shadow.normalBias = options.normalBias;
      }

      if (options.radius !== undefined) {
        light.shadow.radius = options.radius;
      }

      // Configure shadow camera
      if (options.camera) {
        if (light instanceof THREE.DirectionalLight) {
          const camera = light.shadow.camera as THREE.OrthographicCamera;
          const config = options.camera as ShadowCameraConfig;
          if (config.left !== undefined) camera.left = config.left;
          if (config.right !== undefined) camera.right = config.right;
          if (config.top !== undefined) camera.top = config.top;
          if (config.bottom !== undefined) camera.bottom = config.bottom;
          if (config.near !== undefined) camera.near = config.near;
          if (config.far !== undefined) camera.far = config.far;
          camera.updateProjectionMatrix();
        } else {
          const camera = light.shadow.camera as THREE.PerspectiveCamera;
          const config = options.camera as PerspectiveShadowCameraConfig;
          if (config.fov !== undefined) camera.fov = config.fov;
          if (config.aspect !== undefined) camera.aspect = config.aspect;
          if (config.near !== undefined) camera.near = config.near;
          if (config.far !== undefined) camera.far = config.far;
          camera.updateProjectionMatrix();
        }
      }
    }

    return true;
  }

  /** Configure renderer shadow map (enabled, type, autoUpdate). */
  configureRendererShadows(
    renderer: THREE.WebGLRenderer,
    config: RendererShadowConfig = {}
  ): void {
    const {
      enabled = true,
      type = THREE.PCFSoftShadowMap,
      autoUpdate = true,
    } = config;

    renderer.shadowMap.enabled = enabled;
    renderer.shadowMap.type = type as THREE.ShadowMapType;
    renderer.shadowMap.autoUpdate = autoUpdate;
  }

  /** Update shadow maps for all lights that cast shadows. */
  updateShadowMaps(): void {
    this.lights.forEach((meta) => {
      const { light } = meta;
      if (
        (light instanceof THREE.DirectionalLight ||
          light instanceof THREE.PointLight ||
          light instanceof THREE.SpotLight) &&
        light.castShadow
      ) {
        light.shadow.map?.dispose();
        light.shadow.needsUpdate = true;
      }
    });
  }

  // ============================================================================
  // DISPOSAL METHODS
  // ============================================================================

  /** Dispose a single light and its resources. */
  disposeLight(light: THREE.Light): void {
    // Dispose shadow map if it exists
    if (
      light instanceof THREE.DirectionalLight ||
      light instanceof THREE.PointLight ||
      light instanceof THREE.SpotLight
    ) {
      if (light.shadow.map) {
        light.shadow.map.dispose();
      }
    }

    // Dispose light (if it has a dispose method)
    if (typeof (light as any).dispose === "function") {
      (light as any).dispose();
    }
  }

  /** Dispose all lights and clear registry. */
  dispose(): void {
    this.lights.forEach((meta) => {
      this.disposeLight(meta.light);
    });

    this.lights.clear();
    this.nextId = 0;
  }

  /** Dispose all lights; keep module instance. */
  disposeAllLights(): void {
    this.removeAllLights();
  }
}
